import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Toggle from '@/components/toggle';
import Typography from '@/components/typography';
import { getSavedSelectionById } from '@/endpoints/itasca/land/saved-selections/getSavedSelectionById';
import { updateSavedSelection } from '@/endpoints/itasca/land/saved-selections/updateSavedSelection';
import { SavedSelection } from '@/models/land';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useToastContext } from '@/providers/ToastProvider';

const lockToggleId = (id) => `lock-toggle-active-file-${id}`;

type Props = {
  activeFeature?: SavedSelection;
  showOnOffText?: boolean;
  showLabel?: boolean;
  className?: string;
  onSaveCallback?: (save: SavedSelection | null) => void;
};

export const LockToggle: FC<Props> = ({
  activeFeature = null,
  showOnOffText = true,
  showLabel = true,
  className = undefined,
  onSaveCallback = undefined,
}) => {
  const { showErrorToast } = useToastContext();
  const { activeFile, fileManagementUtilities } = useFileManagementContext();
  const feature = useMemo(
    () => activeFeature ?? activeFile,
    [activeFeature, activeFile]
  );

  const defaultActive = useMemo(
    () => feature?.team_editable ?? false,
    [feature?.team_editable]
  );

  const [active, setActive] = useState(defaultActive);

  useEffect(() => {
    setActive(defaultActive);
  }, [defaultActive]);

  const onOffText = useMemo(() => {
    return active ? 'On' : 'Off';
  }, [active]);

  const helpText = useMemo(() => {
    return active ? 'Anyone can edit' : 'Only you can edit';
  }, [active]);

  const updateTeamEditable = useCallback(
    async (teamEditable: boolean) => {
      if (!feature) return null;

      try {
        setActive(teamEditable);
        const { err: updateError } = await updateSavedSelection(feature.id, {
          boundaries: feature.boundaries,
          name: feature.name,
          team_editable: teamEditable,
        });
        if (updateError) throw updateError;

        const { res, err: getError } = await getSavedSelectionById(feature.id);
        if (getError) throw getError;

        if (res?.data) {
          onSaveCallback?.(res.data);
        }
      } catch (error) {
        setActive(!teamEditable);
        showErrorToast('Failed to update team editability.');
      }

      return null;
    },
    [feature, onSaveCallback, showErrorToast]
  );

  if (!fileManagementUtilities?.fileIsCreatedByCurrentUser?.(feature)) {
    return (
      <div className={twMerge('bg-transparent px-4 py-2', className)}>
        <Typography variant="h5">{active ? 'On' : 'Off'}</Typography>
        <Typography variant="body2">
          Set by:{' '}
          {`${feature?.source_user.first_name} ${feature?.source_user.last_name}`}
        </Typography>
      </div>
    );
  }

  return (
    <div className={twMerge('bg-transparent px-4 py-2', className)}>
      <div className="mb-1 flex items-center">
        {showLabel && (
          <Typography className="mr-2 text-gray-100" variant="body2">
            Team Editing
          </Typography>
        )}

        <Toggle
          active={active}
          id={lockToggleId(feature?.id)}
          setActive={updateTeamEditable}
        />
        {showOnOffText && (
          <Typography className="ml-1 text-gray-100" variant="body3">
            {onOffText}
          </Typography>
        )}
      </div>
      <Typography className="text-gray-70" variant="caption">
        {helpText}
      </Typography>
    </div>
  );
};
